import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { Title } from "rbx";
import PropTypes from "prop-types";
import { useTheme, useMediaQuery } from "@mui/material";
import ReviewSection from "./components/ReviewSection/ReviewSection";
import EvaluationForm from "./components/EvaluationForm/EvaluationForm";
import { useModal } from "../../context";
import { usePermissions } from "../../hooks";
import { REVIEW_SECTIONS } from "./constants";
import "./Reviews.scss";
import { GET_REVIEWS, GET_EVALUATION_OWNERS, GET_REVIEWS_WITH_ONLY_EVALUATIONS } from "./graphql";

const IncompleteReviews = ({
  reviews,
  employeeNumber,
  loading,
  setReviews,
  refetchMyReviews,
}) => {
  const permissionLevels = usePermissions();

  const [permissions, setPermissions] = useState(null);
  const [currentReviewState, setCurrentReviewState] = useState(null);
  const [currentEvaluationState, setCurrentEvaluationState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [modalData, setModalData] = useState(null);

  const { data: reviewData, loading: reviewLoading, refetch } = useQuery(GET_REVIEWS, {
    variables: {
      where: {
        id: { equals: currentReviewState?.id },
        evaluations: {
          some: {
            reviewId: { equals: currentReviewState?.id },
            id: { equals: currentEvaluationState?.id }
          }
        }
      },
      evaluationWhere: currentEvaluationState?.id
        ? { id: { equals: currentEvaluationState.id } }
        : undefined,
      sectionsOrderBy: [{ order: "asc" }],
      questionsOrderBy: [{ order: "asc" }],
    },
    skip: !currentReviewState?.id,
    fetchPolicy: "cache-and-network"
  });

  const showManagerReviews =
    permissions?.webEditor ||
    permissions?.superAdmin ||
    permissions?.admin ||
    permissions?.fieldSupervisor;

  const { setModalOpen } = useModal(false);

  const handleComplete = async () => {
    setModalOpen(false);
    await refetchMyReviews();
  };

  useEffect(() => {
    if (permissionLevels) {
      setPermissions(permissionLevels);
    }
  }, [permissionLevels]);

  const handleOpenReview = async (evaluation, isSigning) => {
    

    setIsLoading(true);

    const currentReview = reviews.filter(
      (review) => review.id === evaluation?.reviewId
    );
  

    setCurrentReviewState(currentReview[0]);
    setCurrentEvaluationState({ ...evaluation, isSigning });


    // Force refetch the data when a review is reopened
    if (currentReview[0]) {
      // refetch();
    }
  };

  useEffect(() => {
 
  }, [reviewLoading, reviewData]);

  // Handle modal opening when data arrives - avoids stale closure
  useEffect(() => {
    if (reviewData?.reviews?.[0] && currentEvaluationState && isLoading) {

      const currentEvaluation = reviewData.reviews[0].evaluations.find(
        e => e.id === currentEvaluationState.id
      );

      if (currentEvaluation) {
        setIsLoading(false);
        setModalOpen(
          true,
          <EvaluationForm
            currentReviewId={currentEvaluationState.reviewId}
            employeeNumber={employeeNumber}
            evaluation={currentEvaluation}
            evaluations={reviewData.reviews[0].evaluations}
            isSigning={currentEvaluationState.isSigning}
            key={currentEvaluation.id}
            loading={false}
            refetchMyReviews={refetchMyReviews}
            reviews={reviews}
            onCanceled={() => setModalOpen(false)}
            onComplete={handleComplete}
          />
        );
      } else {
        setIsLoading(false);
      }
    }
  }, [reviewData, currentEvaluationState, isLoading]);

  // Show loading modal when waiting for data
  useEffect(() => {
    if (isLoading && (!reviewData?.reviews?.[0] || !currentEvaluationState)) {
      setModalOpen(
        true,
        <div className="modal-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <div className="loader" />
        </div>
      );
    }
  }, [isLoading, reviewData, currentEvaluationState, setModalOpen]);

  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));

  const className = showManagerReviews
    ? `reviews-outer-container-manager ${downSm && "grid-one-fr"}`
    : `reviews-outer-container ${downSm && "grid-one-fr"}`;

  return (
    <div className={className}>
      {REVIEW_SECTIONS.map(
        (section) =>
          ((showManagerReviews && section.sectionType === "Manager") ||
            section.sectionType !== "Manager") && (
            <div className="reviews-container-section" key={section?.title}>
              <div className="reviews">
                <div className="centered-title" id="employee-reviews">
                  <Title size={4}>{section.title}</Title>
                </div>
                {reviews.length ? (
                  <ReviewSection
                    handleClick={handleOpenReview}
                    refetchMyReviews={refetchMyReviews}
                    reviews={reviews}
                    sectionType={section.sectionType}
                    setReviews={setReviews}
                  />
                ) : (
                  <div className="centered-title">
                    <Title size={6}>
                      Nice! All your {section.sectionType.toLowerCase()}{" "}
                      evaluations are completed
                    </Title>
                  </div>
                )}
              </div>
            </div>
          )
      )}
    </div>
  );
};

IncompleteReviews.propTypes = {
  reviews: PropTypes.array.isRequired,
  employeeNumber: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  setReviews: PropTypes.func.isRequired,
  refetchMyReviews: PropTypes.func,
};

IncompleteReviews.defaultProps = {
  refetchMyReviews: () => { },
};

export default IncompleteReviews;
